import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, ChevronDown } from 'lucide-react';
import { BrutalInput } from '@/components/ui/BrutalInput';
import { cn } from '@/lib/utils';

interface DemographicsMultiSelectProps {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const DemographicsMultiSelect = ({
  options,
  selected,
  onChange,
  placeholder,
  disabled = false,
}: DemographicsMultiSelectProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter options based on search query
  const filteredOptions = options.filter(option => 
    !selected.includes(option.value) &&
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdd = (value: string) => {
    if (!selected.includes(value)) {
      onChange([...selected, value]);
      setSearchQuery('');
    }
  };

  const handleRemove = (value: string) => {
    onChange(selected.filter(v => v !== value));
  };

  const getLabel = (value: string) => {
    return options.find(opt => opt.value === value)?.label || value;
  };

  return (
    <div 
      ref={containerRef}
      className={cn("space-y-3", disabled && "pointer-events-none opacity-50")}
    >
      {/* Selected Items */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-card border-[3px] border-dark">
          {selected.map(value => (
            <div
              key={value}
              className="inline-flex items-center gap-2 bg-primary text-dark px-3 py-2 border-[3px] border-dark font-heading font-bold text-sm"
            >
              <span>{getLabel(value)}</span>
              <button
                type="button"
                onClick={() => handleRemove(value)}
                className="hover:text-destructive transition-colors"
                aria-label={t('common.remove')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <BrutalInput
            type="text"
            placeholder={placeholder || t('common.search')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            className="pr-10"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark/60" />
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />
            
            {/* Results */}
            <div className="absolute z-20 w-full mt-2 bg-card border-[3px] border-dark shadow-[6px_6px_0px_#101322] max-h-[300px] overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="p-4 text-center text-dark/60 font-body">
                  {t('common.noResults')}
                </div>
              ) : (
                <div className="p-2">
                  {filteredOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleAdd(option.value)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-primary hover:text-dark transition-colors text-left font-body font-bold"
                    >
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
